import { Image, ImageSet, Link as BrxLink } from '@bloomreach/spa-sdk';

type ImageLinks = 'site';

interface ImageModel {
  displayName: string;
  fileName: string | null;
  height: number;
  links: Partial<Record<ImageLinks, BrxLink>>;
  mimeType: string;
  name: string;
  size: number;
  width: number;
}

interface ImageSetModel<C extends string[] = []> {
  data: {
    description: string | null;
    displayName: string;
    fileName: string;
    id: string;
    localeString: string | null;
    name: string;
    original: ImageModel | null;
    thumbnail: ImageModel | null;
  } & {
    [key in keyof C]?: Image | null;
  };
}

/**
 * Extension of ImageSet for custom variants support
 */
export interface ImageSetWithVariants<C extends string[]> extends ImageSet {
  model: ImageSetModel<C>;
  original: Image;
  variants: Record<keyof C, Image>;
  getVariant(variant: C[number]): Image | undefined;
}

/**
 * Get image set with custom variants
 *
 * @example
 *
 * // Get original image set from brx page
 * const originalImageSet = props.page?.getContent<ImageSet>(imageRef)
 *
 * // Define custom variant enum
 * enum Variant {
 *  Small = "small",
 *  Large = "large",
 * }
 *
 * // Get image set with custom variants based on the original image set
 * const imageSet = getImageSetWithVariants({
 *  imageSet: originalImageSet,
 *  variants: [Variant.Small, Variant.Large],
 * });
 *
 * // How tou get the original image from the original image set
 * const originalImage = originalImageSet.getOriginal();
 *
 * // Now you can get the custom image variants like so:
 * const smallImage = imageSet.getVariant(Variant.Small);
 * const largeImage = imageSet.getVariant(Variant.Large);
 *
 * // each variant is an Image object like original
 * const originalImageUrl = originalImage?.getUrl();
 * const smallImageUrl = smallImage?.getUrl();
 * const largeImageUrl = largeImage?.getUrl();
 */
export const getImageSetWithVariants = <C extends string[]>({
  imageSet,
  variants,
}: {
  imageSet?: ImageSet;
  variants?: C;
}): ImageSetWithVariants<C> => {
  // Cast image set to ImageSetWithVariants
  const imageSetWithVariants = imageSet as ImageSetWithVariants<C>;

  // Return image set if it's not provided or variants are not defined
  if (!imageSetWithVariants || !variants?.length) {
    return imageSetWithVariants;
  }

  // Create variants object
  imageSetWithVariants.variants = {} as Record<keyof C, Image>;

  // Create variants based on the provided variant keys
  variants?.forEach((variant) => {
    // Create variant based on the original image set
    // and set the model to the variant data
    imageSetWithVariants.variants[variant as keyof C] = Object.create(
      imageSetWithVariants.original,
      {
        model: {
          value: imageSetWithVariants.model.data[variant as keyof C],
          writable: false,
          enumerable: true,
          configurable: true,
        },
      },
    );
  });

  // Get variant by key
  imageSetWithVariants.getVariant = (variant) =>
    imageSetWithVariants.variants[variant as keyof C] || undefined;

  return imageSetWithVariants;
};
